import { useMemo } from 'react';
import { useStudentWeeklyTrend } from '../../../api/hooks';
import type { ClassPerfQuestionRow } from '../../../api/types';
import { EmptyState, ErrorPanel, PagedFooter, PillTabs, Spinner } from '../../../components/ui';
import { FaChartLine } from '../../../components/ui/icons';
import { usePagination } from '../../../lib/usePagination';
import CompareBar from '../components/CompareBar';
import type { MasteryRow } from '../lib/mastery';

export const LOADING = <div className="flex justify-center py-6"><Spinner size="md" /></div>;
export const FAILED = <ErrorPanel />;

export type SubjectKey = number | 'all';

/** The subjects this student has results in, for the subject pills. */
export function useStudentSubjects(id: number) {
  const q = useStudentWeeklyTrend(id, {});
  return useMemo(() => (q.data?.by_subject ?? []).map((s) => ({ id: s.subject_id, name: s.subject_name })), [q.data]);
}

export function SubjectPills({ id, value, onChange, note }: { id: number; value: SubjectKey; onChange: (v: SubjectKey) => void; note?: string }) {
  const subjects = useStudentSubjects(id);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0 max-w-full">
        <PillTabs<string> scroll label="Subject"
          options={[{ key: 'all', label: 'All' }, ...subjects.map((s) => ({ key: String(s.id), label: s.name }))]}
          value={String(value)} onChange={(k) => onChange(k === 'all' ? 'all' : Number(k))} />
      </div>
      {note && <p className="text-xs text-muted">{note}</p>}
    </div>
  );
}

export function subjectLabel(subjects: { id: number; name: string }[], value: SubjectKey) {
  return value === 'all' ? 'All subjects' : (subjects.find((s) => s.id === value)?.name ?? 'Subject');
}

export function PickSubject({ what }: { what: string }) {
  return <EmptyState compact icon={<FaChartLine />} title="Pick a subject" body={`Choose one above to see ${what}.`} />;
}

/** Artboard `.legend`: an orange key for the student and a navy tick for the class. */
export function MasteryLegend({ name, hasClass }: { name: string; hasClass: boolean }) {
  return (
    <span className="flex flex-wrap gap-3.5 text-xs text-muted">
      <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-accent" />{name}</span>
      {hasClass && <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-3 w-[3px] rounded-sm bg-navy dark:bg-ink" />Class</span>}
    </span>
  );
}

/** Artboard `.mrow` list (paged when long): name, "v% / class%" and a track with the class tick. */
export function MasteryList({ rows }: { rows: MasteryRow[] }) {
  const pg = usePagination(rows, 10, rows.length);
  return (
    <div>
      <ul>
        {pg.pageItems.map((r) => (
          <li key={r.key} className="py-2">
            <div className="mb-[7px] flex justify-between gap-2.5 text-[13px]">
              <span className="min-w-0 font-semibold text-ink">{r.name}</span>
              <span className="whitespace-nowrap font-mono font-medium text-ink">
                {r.value.toFixed(0)}%{r.classValue != null && <span className="text-muted"> / {r.classValue.toFixed(0)}%</span>}
              </span>
            </div>
            <CompareBar value={r.value} classValue={r.classValue} label={`${r.name}: ${r.value.toFixed(0)}%${r.classValue != null ? `, class ${r.classValue.toFixed(0)}%` : ''}`} />
          </li>
        ))}
      </ul>
      <PagedFooter className="mt-3.5" label="Mastery pagination" {...pg} onPage={pg.setPage} />
    </div>
  );
}

/** The wrong option most students picked and the correct one, for the "picked X, correct Y" caption. */
export function pickCaption(q: ClassPerfQuestionRow): string | null {
  const correct = q.options.find((o) => o.is_correct);
  const wrong = q.options.filter((o) => !o.is_correct && o.count > 0);
  if (q.question_type !== 'multiple_choice' || !correct || wrong.length === 0) return null;
  const top = wrong.reduce((a, b) => (b.count > a.count ? b : a));
  return `picked ${top.text || top.label}, correct ${correct.text || correct.label}`;
}

/** A small "label / value" figure used across the academics views. */
export function Figure({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'good' | 'bad' }) {
  return (
    <div className="min-w-0 rounded-[14px] border border-line bg-surface px-3.5 py-3">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</p>
      <p className={`mt-1 font-display text-[22px] font-bold leading-tight ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  );
}

/** A plain horizontal bar 0-100 with an optional second (class) tick. */
export function PctBar({ value, classValue, tone = 'accent', label }: { value: number | null; classValue?: number | null; tone?: 'accent' | 'success' | 'danger' | 'slate'; label: string }) {
  const fill = { accent: 'bg-accent', success: 'bg-success', danger: 'bg-danger', slate: 'bg-slate' }[tone];
  return (
    <div role="img" aria-label={label} className="relative h-2.5 rounded-full bg-track">
      {value != null && <div className={`h-full rounded-full ${fill}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />}
      {classValue != null && <span aria-hidden className="absolute -top-1 h-[18px] w-[3px] rounded-sm bg-navy dark:bg-ink" style={{ left: `calc(${Math.max(0, Math.min(100, classValue))}% - 1.5px)` }} />}
    </div>
  );
}

export const pct = (v: number | null | undefined, digits = 0) => (v == null ? '—' : `${v.toFixed(digits)}%`);
